import React from 'react';

import { Link, useLocation } from 'react-router-dom'
import {
  Home,
  FileStack,
  FileText,
  BarChart3,
  Settings,
  Menu,
  X,
  BookOpen,
  Shield,
  AlertTriangle,
  Globe,
  LogIn,
  LogOut,
  User,
  Info,
  GraduationCap,
  Contrast,
  ChevronDown,
  CheckCircle,
  Eye
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

interface LayoutProps {
  children: React.ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const [validatorsOpen, setValidatorsOpen] = React.useState(false)
  const { isAuthenticated, user, logout } = useAuth()

  // Validator submenu items
  const validatorItems = [
    { name: 'WCAG Validator', href: '/wcag-validator', icon: Shield },
    { name: 'ADA Compliance', href: '/ada-compliance-checker', icon: AlertTriangle },
    { name: 'Contrast Checker', href: '/contrast-checker', icon: Contrast },
    { name: 'Color Blindness Simulator', href: '/color-blindness-simulator', icon: Eye },
    { name: 'Accessibility Statement', href: '/accessibility-statement-generator', icon: FileText },
  ]

  // Check if any validator is active
  const isValidatorActive = validatorItems.some(item => location.pathname === item.href)

  const navigation = [
    { name: 'Home', href: '/', icon: Home, protected: false },
    { name: 'Batch Scan', href: '/batch', icon: FileStack, protected: true },
    { name: 'Reports', href: '/reports', icon: FileText, protected: true },
    { name: 'Dashboard', href: '/dashboard', icon: BarChart3, protected: true },
    { name: 'Projects', href: '/projects', icon: Globe, protected: false },
    { name: 'Blog', href: '/blog', icon: BookOpen, protected: false },
    { name: 'Learn', href: '/accessibility-guide', icon: GraduationCap, protected: false },
    { name: 'About', href: '/about', icon: Info, protected: false },
    { name: 'Settings', href: '/settings', icon: Settings, protected: true },
  ]

  const visibleNavigation = navigation.filter(item => !item.protected || isAuthenticated)

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.validators-dropdown')) {
        setValidatorsOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-3">
                <img
                  src="/images/logo.png"
                  alt="Accessibility Checker Logo"
                  className="w-10 h-auto"
                />
                <span className="text-xl font-semibold text-gray-900">
                  Accessibility Checker
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              <nav className="flex items-center space-x-1">
                {/* Home */}
                <Link
                  to="/"
                  className={`
                    flex items-center space-x-1.5 px-2 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap
                    ${location.pathname === '/'
                      ? 'bg-blue-100 text-blue-900'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }
                  `}
                >
                  <Home className="w-4 h-4" />
                  <span>Home</span>
                </Link>

                {/* Our Validators Dropdown */}
                <div
                  className="relative validators-dropdown"
                  onMouseEnter={() => setValidatorsOpen(true)}
                  onMouseLeave={() => setValidatorsOpen(false)}
                >
                  <button
                    className={`
                      flex items-center space-x-1.5 px-2 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap
                      ${isValidatorActive
                        ? 'bg-blue-100 text-blue-900'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }
                    `}
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span>Our Validators</span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${validatorsOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {validatorsOpen && (
                    <div className="absolute top-full left-0 pt-1 w-48 z-50">
                      <div className="bg-white rounded-md shadow-lg border border-gray-200 py-1">
                      {validatorItems.map((item) => {
                        const Icon = item.icon
                        const isActive = location.pathname === item.href
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`
                              flex items-center space-x-2 px-4 py-2 text-sm transition-colors
                              ${isActive
                                ? 'bg-blue-100 text-blue-900'
                                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                              }
                            `}
                          >
                            <Icon className="w-4 h-4" />
                            <span>{item.name}</span>
                          </Link>
                        )
                      })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Rest of navigation */}
                {visibleNavigation.filter(item => item.href !== '/').map((item) => {
                  const Icon = item.icon
                  const isActive = location.pathname === item.href
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`
                        flex items-center space-x-1.5 px-2 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap
                        ${isActive
                          ? 'bg-blue-100 text-blue-900'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        }
                      `}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.name}</span>
                    </Link>
                  )
                })}
              </nav>

              {/* Auth Section */}
              <div className="flex items-center space-x-2 ml-4 border-l border-gray-200 pl-4">
                {isAuthenticated ? (
                  <>
                    <div className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700">
                      <User className="w-4 h-4" />
                      <span>{user?.name}</span>
                    </div>
                    <button
                      onClick={logout}
                      className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                  >
                    <LogIn className="w-4 h-4" />
                    <span>Login</span>
                  </Link>
                )}
              </div>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {/* Home */}
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`
                  flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium transition-colors
                  ${location.pathname === '/'
                    ? 'bg-blue-100 text-blue-900'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }
                `}
              >
                <Home className="w-5 h-5" />
                <span>Home</span>
              </Link>

              {/* Our Validators Section */}
              <div className="px-3 py-2">
                <div className="flex items-center space-x-2 text-sm font-semibold text-gray-500 uppercase tracking-wider">
                  <CheckCircle className="w-4 h-4" />
                  <span>Our Validators</span>
                </div>
                <div className="mt-1 ml-6 space-y-1">
                  {validatorItems.map((item) => {
                    const Icon = item.icon
                    const isActive = location.pathname === item.href
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`
                          flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium transition-colors
                          ${isActive
                            ? 'bg-blue-100 text-blue-900'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                          }
                        `}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.name}</span>
                      </Link>
                    )
                  })}
                </div>
              </div>

              {/* Rest of navigation */}
              {visibleNavigation.filter(item => item.href !== '/').map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.href
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`
                      flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium transition-colors
                      ${isActive
                        ? 'bg-blue-100 text-blue-900'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.name}</span>
                  </Link>
                )
              })}

              {/* Mobile Auth Section */}
              <div className="border-t border-gray-200 pt-2 mt-2">
                {isAuthenticated ? (
                  <>
                    <div className="flex items-center space-x-2 px-3 py-2 text-base font-medium text-gray-700">
                      <User className="w-5 h-5" />
                      <span>{user?.name}</span>
                    </div>
                    <button
                      onClick={() => {
                        logout()
                        setMobileMenuOpen(false)
                      }}
                      className="flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors w-full text-left"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Logout</span>
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                  >
                    <LogIn className="w-5 h-5" />
                    <span>Login</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Tools</h2>
              <ul className="space-y-2">
                {validatorItems.map((item) => (
                  <li key={item.href}>
                    <Link to={item.href} className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      {item.name}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/scan" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                    Scan a Page
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Resources</h2>
              <ul className="space-y-2">
                <li><Link to="/blog" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Blog</Link></li>
                <li><Link to="/accessibility-guide" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Accessibility Guide</Link></li>
                <li><Link to="/projects" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Projects</Link></li>
              </ul>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Company</h2>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">About</Link></li>
              </ul>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Get Started</h2>
              <ul className="space-y-2">
                <li><Link to="/" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Run a Free Scan</Link></li>
                <li><Link to="/wcag-validator" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">WCAG Validator</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-8 pt-6 text-center">
            <p className="text-sm text-gray-500">
              © 2025 Accessibility Checker. Built for WCAG compliance.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
